<template>
  <v-container fluid v-if="event">
    <v-form ref="eventForm">
      <v-row class="pt-0">
        <v-col>
          <v-input :rules="requiredRules"
                   hide-details="auto"
                   v-model="event.name_en">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.name_en') }}</legend>
              <TinyMceEditor v-model="event.name_en"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row class="pt-0">
        <v-col>
          <v-input :rules="requiredRules"
                   hide-details="auto"
                   v-model="event.name_zh">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.name_zh') }}</legend>
              <TinyMceEditor v-model="event.name_zh"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-select readonly
                    :label="$t('admin_event.category')"
                    :items="categoryOptions"
                    v-model="event.category.id"
                    :rules="requiredRules"></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-radio-group :label="$t('admin_event.signup_mode')"
                         v-model="event.signup_mode"
                         :rules="requiredRules"
                         hide-details="auto"
                         readonly>
            <v-radio :label="$t('event_signup_mode.disabled')"
                     :value="-1"></v-radio>
            <v-radio :label="$t('event_signup_mode.single_session')"
                     :value="1"></v-radio>
            <v-radio :label="$t('event_signup_mode.multi_session')"
                     :value="2"></v-radio>
            <v-radio :label="$t('event_signup_mode.multi_series')"
                     :value="3"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row v-if="event.signup_mode === -1 || event.signup_mode === 1"
             class="mt-4">
        <v-col>
          <DatePickerInput :label="$t('admin_event.date')"
                           v-model="event.date"
                           :rules="requiredRules"
                           hide-details="auto"></DatePickerInput>
        </v-col>
      </v-row>
      <v-row v-if="event.signup_mode === -1 || event.signup_mode === 1">
        <v-col>
          <TimePickerInput outlined
                           :label="$t('admin_event.start_time')"
                           v-model="event.from_time"
                           :rules="requiredRules"
                           hide-details="auto"></TimePickerInput>
        </v-col>
        <v-col>
          <TimePickerInput outlined
                           :label="$t('admin_event.end_time')"
                           v-model="event.to_time"
                           :rules="requiredRules"
                           hide-details="auto"></TimePickerInput>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input :rules="requiredRules"
                   hide-details="auto"
                   v-model="event.visible_from">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.visible_from') }}</legend>
              <DateTimePickerInput v-model="event.visible_from"
                                   hide-details></DateTimePickerInput>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row v-if="event.signup_mode !== -1">
        <v-col>
          <v-input :rules="requiredRules"
                   hide-details="auto"
                   v-model="event.signup_until">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.signup_until') }}</legend>
              <DateTimePickerInput v-model="event.signup_until"
                                   hide-details></DateTimePickerInput>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input :rules="[
                              value => (visibleForever || !!value) || $t('register.required_fields'),
                            ]"
                   hide-details="auto"
                   v-model="event.visible_until">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.visible_until') }}</legend>
              <v-row>
                <v-col cols="2">
                  <v-checkbox :label="$t('admin_event.visible_forever')"
                              v-model="visibleForever"></v-checkbox>
                </v-col>
                <v-col>
                  <DateTimePickerInput v-model="event.visible_until"
                                       hide-details
                                       :disabled="visibleForever"></DateTimePickerInput>
                </v-col>
              </v-row>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-radio-group :label="$t('admin_event.fee')"
                         v-model="feeMode"
                         :rules="requiredRules"
                         hide-details="auto">
            <v-radio :label="$t('event.free')"
                     :value="'FREE'"></v-radio>
            <v-radio :label="$t('admin_event.fixed_fee')"
                     :value="'FIXED'"></v-radio>
            <v-radio :label="$t('admin_event.fee_with_tiers')"
                     :value="'WITH_TIERS'"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row v-if="feeMode === 'FIXED'">
        <v-col>
          <v-text-field outlined
                        :label="$t('admin_event.fee')"
                        v-model="fee"
                        type="number"
                        prefix="HKD"
                        :rules="fixedFeeRules"
                        hide-details="auto"></v-text-field>
        </v-col>
      </v-row>
      <v-row v-if="feeMode === 'WITH_TIERS'">
        <v-col>
          <v-input :rules="feeTierRules"
                   v-model="feeTiers">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_event.fee_tier') }}</legend>
              <v-row v-for="(tier, idx) in feeTiers"
                     :key="idx">
                <v-col>
                  <v-text-field outlined
                                :label="$t('admin_event.fee_tier_en')"
                                v-model="tier.name_en"
                                :rules="requiredRules"
                                hide-details="auto"></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field outlined
                                :label="$t('admin_event.fee_tier_zh')"
                                v-model="tier.name_zh"
                                :rules="requiredRules"
                                hide-details="auto"></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field outlined
                                :label="$t('admin_event.fee')"
                                v-model="tier.fee"
                                :rules="requiredRules"
                                hide-details="auto"></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field outlined
                                :label="$t('admin_event.fee_tier_max')"
                                v-model="tier.max"
                                :rules="requiredRules"
                                hide-details="auto"></v-text-field>
                </v-col>
                <v-col cols="1" class="align-content-center">
                  <v-btn icon @click="removeFeeTier(idx)">
                    <v-icon>fas fa-times</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn block @click="addFeeTier">{{ $t('admin_event.add_fee_tier') }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row v-if="event.signup_mode === 1"
             class="mt-4">
        <v-col>
          <v-text-field outlined
                        :label="$t('admin_event.quota')"
                        type="number"
                        v-model="event.quota"
                        :rules="[
                              value => (unlimitedQuota || !!value) || $t('register.required_fields'),
                            ]"
                        hide-details="auto"
                        :disabled="unlimitedQuota">
          </v-text-field>
        </v-col>
        <v-col cols="2">
          <v-checkbox :label="$t('unlimited')"
                      v-model="unlimitedQuota"></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field outlined
                        :label="$t('admin_event.location_en')"
                        v-model="event.location_en"
                        :rules="requiredRules"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field outlined
                        :label="$t('admin_event.location_zh')"
                        v-model="event.location_zh"
                        :rules="requiredRules"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input :rules="[value => value.length !== 0 || $t('register.required_fields')]"
                   hide-details="auto"
                   v-model="event.languages">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.languages') }}</legend>
              <v-row>
                <v-col>
                  <v-checkbox v-model="event.languages"
                              :label="$t('languages.en')"
                              value="en"
                              hide-details></v-checkbox>
                </v-col>
                <v-col>
                  <v-checkbox v-model="event.languages"
                              :label="$t('languages.zh-Hant')"
                              value="zh-Hant"
                              hide-details></v-checkbox>
                </v-col>
                <v-col>
                  <v-checkbox v-model="event.languages"
                              :label="$t('languages.zh-Hans')"
                              value="zh-Hans"
                              hide-details></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row class="pt-0">
        <v-col>
          <v-input hide-details
                   v-model="event.remarks_en">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.remarks_en') }}</legend>
              <TinyMceEditor v-model="event.remarks_en"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row class="pt-0">
        <v-col>
          <v-input hide-details
                   v-model="event.remarks_zh">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.remarks_zh') }}</legend>
              <TinyMceEditor v-model="event.remarks_zh"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input hide-details="auto"
                   v-model="event.banner"
                   :rules="requiredRules">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.banner') }}</legend>
              <PhotoPreviewInput v-model="event.banner"
                                 div-class="event-banner"
                                 file-name="event-banner"></PhotoPreviewInput>
              <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>2545px(W) X 1081px(H)</b></small>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row class="pt-0">
        <v-col>
          <v-input hide-details
                   v-model="event.description_en">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.description_en') }}</legend>
              <TinyMceEditor v-model="event.description_en"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row class="pt-0">
        <v-col>
          <v-input hide-details
                   v-model="event.description_zh">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.description_zh') }}</legend>
              <TinyMceEditor v-model="event.description_zh"></TinyMceEditor>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-file-input :label="$t('admin_event.poster')"
                        accept="image/jpeg, image/png, image/jpg, application/pdf"
                        hint="ACCEPTED FILE FORMAT: PNG, JPEG, JPG, PDF"
                        persistent-hint
                        clearable
                        v-model="event.poster">
            <template #selection="{file, text}">
              {{ text || file }}
            </template>
            <template #append-outer>
              <a :href="getURL(event.poster)"
                 target="_blank"
                 v-if="event.poster">
                <v-icon>fas fa-search</v-icon>
              </a>
            </template>
          </v-file-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-file-input :label="$t('admin_event.attachment')"
                        accept="image/jpeg, image/png, image/jpg, application/pdf"
                        hint="ACCEPTED FILE FORMAT: PNG, JPEG, JPG, PDF"
                        persistent-hint
                        clearable
                        v-model="event.attachment_id">
            <template #selection="{file, text}">
              {{ text || file }}
            </template>
            <template #append-outer>
              <a :href="getURL(event.attachment_id)"
                 target="_blank"
                 v-if="event.attachment_id">
                <v-icon>fas fa-search</v-icon>
              </a>
            </template>
          </v-file-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-input hide-details="auto">
            <v-container fluid class="flex-column">
              <legend class="v-label theme--light">{{ $t('admin_event.images') }}</legend>
              <!--                  <v-file-input :label="$t('admin_event.images')"-->
              <!--                                accept="image/jpeg, image/png, image/jpg"-->
              <!--                                ref="imageFileInput"-->
              <!--                                @input="onImageUpload"></v-file-input>-->
              <input type="file"
                     accept="image/jpeg, image/png, image/jpg"
                     hidden
                     ref="imageFileInput"
                     @input="onImageUpload">
              <PhotoCarousel v-model="images"
                             show-placeholder
                             @placeholder="$refs.imageFileInput.click()"
                             @delete="deleteImage"></PhotoCarousel>
              <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>1013(W) X 1287(H)</b> (MEGA EVENT: <b>2371(W) X 787 (H)</b>)</small>
            </v-container>
          </v-input>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import TinyMceEditor from "@/components/TinyMceEditor";
import DatePickerInput from "@/components/DatePickerInput";
import TimePickerInput from "@/components/TimePickerInput";
import DateTimePickerInput from "@/components/DateTimePickerInput";
import PhotoPreviewInput from "@/components/PhotoPreviewInput";
import PhotoCarousel from "@/components/PhotoCarousel";
import {computed, onMounted, watch, ref} from "@vue/composition-api";
import {DateTime} from "luxon";
import _ from "lodash";
import uuid4 from "uuid/v4";

export default {
  name: 'EventInfo',
  components: {PhotoCarousel, PhotoPreviewInput, DateTimePickerInput, TimePickerInput, DatePickerInput, TinyMceEditor},
  props: {
    eventId: String,
    save: Boolean,
  },
  setup(props, {root, emit}) {
    onMounted(() => {
      getEvent();
      getCategories();

      emit('update:save', false);
    })

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const eventForm = ref(null);

    // Info
    const event = ref(null);
    const getEvent = function () {
      root.$callAPI('hkiarace/admin/event/', {id: props.eventId}, function (response) {
        event.value = {
          ...response.body.event,
          languages: JSON.parse(response.body.event.languages),
          from_time: response.body.event.from_time ? DateTime.fromFormat(response.body.event.from_time, 'HH:mm:ss').toFormat('HH:mm') : null,
          to_time: response.body.event.to_time ? DateTime.fromFormat(response.body.event.to_time, 'HH:mm:ss').toFormat('HH:mm') : null,
        };

        visibleForever.value = !response.body.event.visible_until;
        feeTiers.value = JSON.parse(response.body.event.fee_tiers);
        if (feeTiers.value.length === 0) {
          feeMode.value = 'FREE';
        } else if (feeTiers.value[0].name_en === null) {
          feeMode.value = 'FIXED';
          fee.value = parseInt(feeTiers.value[0].fee);
        } else {
          feeMode.value = 'WITH_TIERS';
        }

        unlimitedQuota.value = response.body.event.quota === -1;

        images.value = JSON.parse(response.body.event.images);
      });
    };
    const categories = ref([]);
    const categoryOptions = computed(() => {
      return categories.value.map((c) => {
        return {text: c.name, value: c.id};
      })
    })
    const getCategories = function () {
      root.$callAPI('hkiarace/event_category/', {}, function (response) {
        categories.value = response.body.categories;
      });
    };

    const visibleForever = ref(false);
    const feeMode = ref('FREE');
    const fee = ref(null);
    const fixedFeeRules = ref([
      value => (feeMode.value !== 'FIXED' || !!value) || root.$t('register.required_fields'),
    ])
    const feeTiers = ref([]);
    const feeTierRules = ref([
      value => (feeMode.value !== 'WITH_TIERS' || !!value.length) || root.$t('register.required_fields'),
    ])
    const addFeeTier = function () {
      feeTiers.value = [
        ...feeTiers.value,
        {id: uuid4(), name_en: null, name_zh: null, fee: null, max: null},
      ]
    }
    const removeFeeTier = function (idx) {
      feeTiers.value.splice(idx, 1);
      if (feeTiers.value.length === 0) {
        feeTiers.value.push({id: uuid4(), name_en: null, name_zh: null, fee: null, max: null});
      }
    }

    const unlimitedQuota = ref(false);

    const getURL = function (f) {
      if (f instanceof File) {
        return URL.createObjectURL(f);
      } else {
        return 'api/static/' + f;
      }
    };
    const images = ref([]);
    const imageFileInput = ref(null);
    const onImageUpload = function (e) {
      let input = e.target;
      let f = input.files[0];
      images.value.push(f);
      input.value = '';
    }
    const deleteImage = function (idx) {
      images.value.splice(idx, 1);
    };

    const canSave = ref(true);
    const save = async function () {
      if (!eventForm.value.validate()) {
        root.$showAlertMessage(root.$t('admin_event.invalid_form_msg'));
        return
      }

      let start = event.value.visible_from;
      let end = event.value.signup_until;
      if (event.value.signup_mode === -1 || event.value.signup_mode === 1) {
        let date = DateTime.fromISO(event.value.date);
        let from_time = DateTime.fromISO(event.value.from_time);
        let to_time = DateTime.fromISO(event.value.to_time);

        start = DateTime.fromObject({
          year: date.year,
          month: date.month,
          day: date.day,
          hour: from_time.hour,
          minute: from_time.minute,
        }).toISO();

        end = DateTime.fromObject({
          year: date.year,
          month: date.month,
          day: date.day,
          hour: to_time.hour,
          minute: to_time.minute,
        }).toISO();
      }

      let fee_tiers = '[]';
      if (feeMode.value === 'FIXED') {
        fee_tiers = JSON.stringify([{name_en: null, name_zh: null, fee: fee.value}]);
      } else if (feeMode.value === 'WITH_TIERS') {
        fee_tiers = JSON.stringify(_.filter(feeTiers.value, function (t) {
          return t.name_en && t.name_en.length && t.fee;
        }));
      }

      let params = {
        ...event.value,
        category_id: event.value.category.id,

        start,
        end,
        visible_until: visibleForever.value ? null : event.value.visible_until,
        signup_from: event.value.signup_from,
        signup_until: event.value.signup_until,
        quota: unlimitedQuota.value ? -1 : event.value.quota,
        languages: JSON.stringify(event.value.languages),
        fee_tiers,

        banner: event.value.banner instanceof File ? await root.$convertFileToBase64(event.value.banner) : event.value.banner,
        poster: event.value.poster instanceof File ? await root.$convertFileToBase64(event.value.poster) : event.value.poster,
        attachment: event.value.attachment_id instanceof File ? await root.$convertFileToBase64(event.value.attachment_id) : event.value.attachment_id,
        images: await Promise.all(images.value.map(async (i) => {
          if (i instanceof File) {
            return await root.$convertFileToBase64(i)
          } else {
            return i;
          }
        })),
      }
      root.$callAPI('hkiarace/admin/event/edit/', params, () => {
        root.$showAlertMessage(root.$t('admin_event.save_success_msg'));
        emit('finish');
      });
    };

    watch(() => props.save, (newValue) => {
      if (newValue) {
        emit('update:save', false);
        save();
      }
    })

    watch(() => props.eventId, () => {
      getEvent();
    })

    return {
      requiredRules,
      eventForm,

      // Info
      event,

      categoryOptions,

      visibleForever,

      feeMode,
      fee,
      fixedFeeRules,
      feeTiers,
      feeTierRules,
      addFeeTier,
      removeFeeTier,

      unlimitedQuota,

      getURL,
      images,
      imageFileInput,
      onImageUpload,
      deleteImage,

      canSave,
      // save,
    }
  },
}
</script>
